'use client'

import { useMemo } from 'react'
import ArrowOutwardIcon from '@mui/icons-material/ArrowOutward'
import { parseSiteData, createPageEntry } from '@biolumina/ui/util/site.utils'
import { Break } from '@biolumina/ui/components/break/break'
import { TRUSTED } from '@biolumina/ui/containers/modals/modal.enums'

export const useSite = () => {
  const { pageData, nav } = useMemo(() => {
    return parseSiteData({
      ...createPageEntry('home', {
        display: <>Home</>,
        pageDisplay: <>Home</>,
        nav: false,
        banner: false,
        showIndication: false,
      }),
      ...createPageEntry('about-ros1-nsclc', {
        display: <>About ROS1+ NSCLC</>,
        pageDisplay: <>About ROS1+ NSCLC</>,
        children: [
          {
            href: '/about-ros1-nsclc#understanding-nsclc',
            display: <>Understanding NSCLC</>,
          },
          {
            href: '/about-ros1-nsclc#biomarker-testing',
            display: <>Biomarker Testing</>,
          },
          {
            href: '/about-ros1-nsclc#what-is-ros1-nsclc',
            display: <>What is ROS1+ NSCLC?</>,
          },
          {
            href: '/about-ros1-nsclc#summary',
            display: <>Summary</>,
          },
        ],
      }),
      ...createPageEntry('about-ibtrozi', {
        display: <>About IBTROZI</>,
        removePartOfTheIsi: true,
        bannerMode: 'pair',
        pageDisplay: <>About IBTROZI<sup>&reg;</sup> (taletrectinib)</>,
        children: [
          {
            href: '/about-ibtrozi#study-overview',
            display: <>Study Overview</>,
          },
          {
            href: '/about-ibtrozi#clinical-study-results',
            display: <>Clinical Study Results</>,
          },
          {
            href: '/about-ibtrozi#safety-considerations',
            display: <>Safety Considerations</>,
          },
          {
            href: '/about-ibtrozi#summary',
            display: <>Summary</>,
          },
        ],
      }),
      ...createPageEntry('taking-ibtrozi', {
        display: <>Taking IBTROZI</>,
        pageDisplay: <>Taking IBTROZI<sup>&reg;</sup> (taletrectinib)</>,
        bannerMode: 'pair',
        children: [
          {
            href: '/taking-ibtrozi#getting-started',
            display: <>Getting Started</>,
          },
          {
            href: '/taking-ibtrozi#how-to-take-IBTROZI',
            display: <>How to Take IBTROZI</>,
          },
          {
            href: '/taking-ibtrozi#summary',
            display: <>Summary</>,
          },
        ]
      }),
      ...createPageEntry('patient-caregiver-resources', {
        display: <>Patient and Caregiver Resources </>,
        pageDisplay: <>Patient and Caregiver Resources </>,
        children: [
          {
            href: '/patient-caregiver-resources#downloads',
            display: <>Downloads</>,
          },
          {
            href: '/patient-caregiver-resources#advocacy-community-support',
            display: <>Advocacy and community support</>,
          },
          {
            href: '/patient-caregiver-resources#faqs',
            display: <>FAQs</>,
          },
          {
            href: '/patient-caregiver-resources#glossary',
            display: <>Glossary</>,
          },
        ],
      }),
      ...createPageEntry('patient-support', {
        display: <>Patient Support</>,
        pageDisplay: <>Patient Support</>,
        children: [
          {
            href: '/patient-support#nuvation-connect',
            display: <>NuvationConnect™</>,
          },
          {
            href: '/patient-support#specialty-pharmacy',
            display: <>Specialty Pharmacy</>,
          },
        ],
      }),
      ...createPageEntry('sitemap', {
        display: <>Sitemap</>,
        pageDisplay: <>Sitemap</>,
        nav: false,
        banner: false,
        pageNav: false,
        isi: false,
        sitemap: false,
        showGreenBar: true,
      }),
      ...createPageEntry('not-found', {
        nav: false,
        banner: false,
        isi: false,
        pageNav: false,
        pageDisplay: <>Not Found</>,
      }),
    })
  }, [])

  const pdfs = {
    prescribingInfo: 'https://ibtrozi-pi.com',
    patientInfo: 'https://ibtrozi-pi.com/patient-information',
  }

  const topLinks = useMemo(
    () => [
      { href: '#isi', display: 'Important Safety Information', icon: null },
      {
        href: pdfs.prescribingInfo,
        display: 'Prescribing Information',
        target: '_blank',
        icon: null,
      },
      {
        href: pdfs.patientInfo,
        display: 'Patient Information',
        target: '_blank',
        icon: null,
      },
      {
        href: 'https://ibtrozihcp.com',
        external: true,
        prompt: TRUSTED,
        target: '_blank',
        strokeWidth: 0,
        display: <>For&nbsp;Healthcare Professionals</>,
        icon: ArrowOutwardIcon,
      },
    ],
    [pdfs.patientInfo, pdfs.prescribingInfo]
  )

  const footerLinks = useMemo(
    () => [
      {
        href: 'https://www.nuvationbio.com/contact/',
        external: false,
        target: '_blank',
        display: <>Contact Us</>,
      },
      {
        href: 'https://www.nuvationbio.com/privacy-notice/',
        external: false,
        target: '_blank',
        display: <>Privacy Notice</>,
      },
      {
        href: 'https://www.nuvationbio.com/terms-of-use',
        external: false,
        target: '_blank',
        display: <>Legal Statement</>,
      },
      {
        href: '/sitemap',
        display: <>Sitemap</>,
        external: false,
        target: '_self',
      },
      {
        href: '#',
        external: true,
        OneTrust: true,
        className: 'open-cookie-preferences',
        target: '_self',
        display: <>Your Privacy Choices</>,
      },
    ],
    []
  )

  return {
    //root: 'https://www.ibtrozi.com',
    code: 'MAT\u2011IBT\u20110072',
    copy: 'IBTROZI, the IBTROZI logo, NuvationConnect, Nuvation Bio, and the Nuvation Bio logo are trademarks of Nuvation Bio Inc. All other marks are the property of their respective owners.',
    date: '07/2026',
    nav,
    pdfs,
    pageData,
    topLinks,
    footerLinks,
  }
}
